/**
 * All widget styling lives inside the shadow root.
 *
 * The host page is arbitrary — a proposal with its own reset, a microsite with
 * aggressive global selectors — and the overlay has to look the same on all of
 * them. A shadow root is the only way to get that without an !important war.
 */
export const CSS = `
:host {
  all: initial;
  --a-accent: #c2410c;
  --a-accent-soft: rgba(194, 65, 12, 0.14);
  --a-ink: #1a1a18;
  --a-ink-soft: #45443f;
  --a-muted: #78766f;
  --a-panel: #ffffff;
  --a-line: #e4e2dd;
  --a-line-soft: #efedea;
  --a-shadow: 0 8px 30px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.07);

  /* Same scale as the dashboard, one step down: this type lives in small
     floating boxes over someone else's content, so it has to stay compact. */
  --a-micro: 11px;
  --a-small: 12.5px;
  --a-body: 13.5px;

  /* Deliberately the OS UI font, not the dashboard's Inter.
     The widget is injected into arbitrary pages, where a webfont either needs a
     network request a strict CSP can block — failing silently, so it would look
     right here and wrong on a client's machine — or 15-40KB of base64 on top of
     a 30KB bundle. At these sizes the system UI font is genuinely the right
     tool, and elegance comes from spacing and weight instead. */
  font: var(--a-body)/1.55 ui-sans-serif, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: var(--a-ink);
  -webkit-font-smoothing: antialiased;
}

@media (prefers-color-scheme: dark) {
  :host {
    --a-ink: #eeece8;
    --a-ink-soft: #c9c6c0;
    --a-muted: #94918a;
    --a-panel: #212126;
    --a-line: #33333a;
    --a-line-soft: #2a2a30;
    --a-accent: #fb923c;
    --a-accent-soft: rgba(251, 146, 60, 0.18);
  }
}

* { box-sizing: border-box; }

/* Marker numbers, times and counts all sit in fixed positions and change in
   place — tabular figures stop them shuffling as they update. */
.marker, .when, .pill, .badge, .tool { font-variant-numeric: tabular-nums; }

button {
  font: inherit;
  color: inherit;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 0;
}

.layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 2147483000;
}

/* ── markers ─────────────────────────────────────────────────────────── */

.marker {
  position: absolute;
  transform: translate(-50%, -100%);
  min-width: 26px;
  height: 26px;
  padding: 0 7px;
  border-radius: 13px 13px 13px 3px;
  background: var(--a-accent);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  box-shadow: var(--a-shadow);
  transition: transform 0.1s ease;
}

.marker:hover,
.marker.active {
  transform: translate(-50%, -100%) scale(1.12);
}

/* Status is carried by colour on the marker, so a page full of pins reads at a
   glance without opening any of them. */
.marker.tone-open { background: var(--a-accent); }
.marker.tone-progress { background: #b45309; }
.marker.tone-review { background: #7c3aed; }
.marker.tone-done { background: #15803d; }
.marker.tone-dropped { background: var(--a-muted); opacity: 0.6; }

.badge {
  font-size: var(--a-micro);
  font-weight: 550;
  letter-spacing: 0.01em;
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid currentColor;
  white-space: nowrap;
}

.badge.tone-open { color: var(--a-accent); }
.badge.tone-progress { color: #b45309; }
.badge.tone-review { color: #7c3aed; }
.badge.tone-done { color: #15803d; }
.badge.tone-dropped { color: var(--a-muted); }

.event {
  font-size: var(--a-micro);
  color: var(--a-muted);
  font-style: italic;
  line-height: 1.45;
  padding-top: 9px;
  border-top: 1px solid var(--a-line-soft);
  margin-top: 11px;
}

select.status-picker {
  font: inherit;
  font-size: var(--a-small);
  padding: 6px 9px;
  border-radius: 8px;
  border: 1px solid var(--a-line);
  background: var(--a-panel);
  color: var(--a-ink);
}

select.status-picker:disabled { opacity: 0.5; }

.hl {
  position: absolute;
  background: var(--a-accent-soft);
  border-bottom: 2px solid var(--a-accent);
  pointer-events: none;
  border-radius: 2px;
}

.region-box {
  position: absolute;
  border: 2px solid var(--a-accent);
  background: var(--a-accent-soft);
  border-radius: 4px;
  pointer-events: none;
}

.drag-box {
  position: fixed;
  border: 2px dashed var(--a-accent);
  background: var(--a-accent-soft);
  border-radius: 4px;
  pointer-events: none;
  z-index: 2147483004;
}

/* ── launcher + toolbar ──────────────────────────────────────────────── */

.pill {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2147483005;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 17px;
  border-radius: 20px;
  background: var(--a-panel);
  border: 1px solid var(--a-line);
  box-shadow: var(--a-shadow);
  font-weight: 550;
  font-size: var(--a-small);
  letter-spacing: -0.002em;
  transition: border-color 0.12s ease, color 0.12s ease;
}

.pill:hover { border-color: var(--a-accent); color: var(--a-accent); }
.pill .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--a-accent); }

.toolbar {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2147483005;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 5px;
  border-radius: 13px;
  background: var(--a-panel);
  border: 1px solid var(--a-line);
  box-shadow: var(--a-shadow);
}

.tool {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 31px;
  padding: 0 12px;
  border-radius: 9px;
  font-size: var(--a-small);
  font-weight: 500;
  letter-spacing: -0.002em;
  white-space: nowrap;
  transition: background 0.12s ease;
}

.tool:hover { background: var(--a-accent-soft); }
.tool.on { background: var(--a-accent); color: #fff; }
.tool.icon { padding: 0 10px; color: var(--a-muted); }
.sep { width: 1px; height: 20px; background: var(--a-line); margin: 0 5px; }

/* ── popover ─────────────────────────────────────────────────────────── */

.popover {
  position: fixed;
  z-index: 2147483006;
  width: 336px;
  max-height: 70vh;
  overflow-y: auto;
  padding: 16px 18px;
  border-radius: 14px;
  background: var(--a-panel);
  border: 1px solid var(--a-line);
  box-shadow: var(--a-shadow);
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.num {
  font-weight: 700;
  font-size: var(--a-small);
  letter-spacing: 0.01em;
  color: var(--a-accent);
}

.head-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Quiet until you look for it. It is the way out of a panel, not an action worth
   drawing attention away from the comment box. */
.dismiss {
  all: unset;
  cursor: pointer;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 6px;
  font-size: 13px;
  color: var(--a-muted);
}

.dismiss:hover {
  color: var(--a-text);
  background: var(--a-line);
}

.quote {
  border-left: 2px solid var(--a-line);
  padding-left: 11px;
  color: var(--a-muted);
  font-size: var(--a-small);
  line-height: 1.5;
  margin-bottom: 14px;
  max-height: 62px;
  overflow: hidden;
}

.msg { margin-bottom: 13px; }
.msg + .msg { border-top: 1px solid var(--a-line-soft); padding-top: 13px; }
.who { font-weight: 600; font-size: var(--a-small); letter-spacing: -0.002em; }
.when {
  color: var(--a-muted);
  font-weight: 400;
  margin-left: 7px;
  font-size: var(--a-micro);
}
.text {
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--a-ink-soft);
  margin-top: 3px;
}

textarea, input {
  font: inherit;
  font-size: var(--a-small);
  width: 100%;
  padding: 9px 11px;
  border-radius: 9px;
  border: 1px solid var(--a-line);
  background: transparent;
  color: var(--a-ink);
  resize: vertical;
}

textarea::placeholder, input::placeholder { color: var(--a-muted); }
textarea:focus, input:focus { outline: 2px solid var(--a-accent); outline-offset: -1px; }
textarea { min-height: 70px; line-height: 1.5; }
input { margin-bottom: 8px; }

.actions {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 11px;
}

.primary {
  background: var(--a-accent);
  color: #fff;
  padding: 8px 15px;
  border-radius: 9px;
  font-weight: 600;
  font-size: var(--a-small);
  letter-spacing: -0.002em;
}

.primary:disabled { opacity: 0.4; cursor: default; }
.ghost { color: var(--a-muted); font-size: var(--a-small); padding: 8px 4px; }
.ghost:hover { color: var(--a-accent); }
.spacer { flex: 1; }

.note {
  margin-top: 11px;
  padding: 8px 11px;
  border-radius: 8px;
  font-size: var(--a-small);
  line-height: 1.45;
  background: var(--a-accent-soft);
  color: var(--a-ink);
}

/* ── sidebar ─────────────────────────────────────────────────────────── */

.sidebar {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  z-index: 2147483004;
  background: var(--a-panel);
  border-left: 1px solid var(--a-line);
  box-shadow: var(--a-shadow);
  display: flex;
  flex-direction: column;
}

.sidebar h3 {
  margin: 0;
  padding: 17px 18px 13px;
  font-size: var(--a-micro);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.075em;
  color: var(--a-muted);
  border-bottom: 1px solid var(--a-line);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.list { overflow-y: auto; padding: 9px; flex: 1; }

/* Behind the funnel, so the default sidebar stays a list of comments rather than
   a control panel with some comments underneath it. */
.filters {
  padding: 12px 14px;
  border-bottom: 1px solid var(--a-line);
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.filter-field > span {
  font-size: var(--a-micro);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--a-muted);
}

.filter-field select {
  width: 100%;
  font: inherit;
  font-size: var(--a-small);
  padding: 5px 7px;
  border-radius: 7px;
  border: 1px solid var(--a-line);
  background: var(--a-panel);
  color: var(--a-text);
}

.ghost.on { color: var(--a-accent); }

.filter-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  line-height: 1;
}

.item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 11px;
  border-radius: 9px;
  margin-bottom: 2px;
  transition: background 0.12s ease;
}

.item:hover { background: var(--a-accent-soft); }
.item .who { display: block; margin-bottom: 1px; }
.item .snip {
  color: var(--a-muted);
  font-size: var(--a-small);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.group {
  padding: 15px 11px 6px;
  font-size: var(--a-micro);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--a-muted);
}

.orphan-why {
  display: block;
  color: var(--a-muted);
  font-size: var(--a-micro);
  font-style: italic;
  margin-top: 3px;
}

.blank {
  padding: 34px 18px;
  color: var(--a-muted);
  text-align: center;
  font-size: var(--a-small);
  line-height: 1.55;
}

.toast {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 2147483007;
  padding: 10px 17px;
  border-radius: 10px;
  background: var(--a-ink);
  color: var(--a-panel);
  font-size: var(--a-small);
  line-height: 1.45;
  max-width: 70vw;
  box-shadow: var(--a-shadow);
}
`;
